import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  updateEventAction,
  getEventAction,
} from "../../redux/actions/eventAction";
import {
  Box,
  TextField,
  Button,
  FormControl,
  Grid,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useGetCustomersQuery } from "../../state/api";
import Autocomplete from "@mui/material/Autocomplete";
import ListItemText from "@mui/material/ListItemText";
import Header from "../../components/Header";
import { toast } from "react-toastify";
import LoadingIndicator from "../../components/loadingIndicator";
import * as yup from "yup";

const validationSchema = yup.object().shape({
  eventName: yup.string().required("Event name is required"),
  date: yup
    .date()
    .nullable()
    .typeError("Date is required")
    .required("Date is required"),
  startTime: yup
    .string()
    .required("Starting time is required")
    .matches(
      /^(0?[1-9]|1[0-2]):[0-5][0-9] [APap][mM]$/,
      "Invalid time format, it should be HH:MM AM or PM"
    ),
  endTime: yup
    .string()
    .required("Ending time is required")
    .matches(
      /^(0?[1-9]|1[0-2]):[0-5][0-9] [APap][mM]$/,
      "Invalid time format, it should be HH:MM AM or PM"
    )
    .test(
      "is-end-time-greater",
      "End time must be greater than start time",
      function (value) {
        const { startTime } = this.parent;
        if (!startTime || !value) {
          return true; // Let other validations handle empty fields
        }

        // Convert both start and end times to 24-hour format for comparison
        const parseTime = (time) => {
          const [hour, minute, ampm] = time.split(/[:\s]/);
          let parsedHour = parseInt(hour, 10);
          if (ampm.toUpperCase() === "PM" && parsedHour !== 12) {
            parsedHour += 12;
          } else if (ampm.toUpperCase() === "AM" && parsedHour === 12) {
            parsedHour = 0;
          }
          return `${parsedHour.toString().padStart(2, "0")}:${minute}`;
        };

        const parsedStartTime = parseTime(startTime);
        const parsedEndTime = parseTime(value);

        return parsedStartTime < parsedEndTime;
      }
    ),
  location: yup.string().required("Location is required"),
  // users: yup.array().test({
  //   name: "at-least-one-celebrity",
  //   test: (value) => {
  //     return value && value.length > 0;
  //   },
  //   message: "At least one Celebrity is required",
  // }),
});

const EditEvent = () => {
  const location = useLocation();
  const { pathname } = location;
  const eventId = pathname.split("/").pop();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    eventName: "",
    date: "",
    startTime: "",
    endTime: "",
    location: "",
    users: [],
    description: "",
  });

  const { data: usersList = [], isLoading, isError } = useGetCustomersQuery();
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        const response = await dispatch(getEventAction(eventId));
        if (response && response.success) {
          const eventDetails = response.response;
          eventDetails.date = eventDetails.date.slice(0, 10);

          // Split the users string into an array
          const userNames = eventDetails.users[0].split(",");

          // Map user names to user objects from usersList
          const usersFromEvent = userNames
            .map((userName) => {
              return usersList.find(
                (user) => user.fullname === userName.trim()
              );
            })
            .filter((user) => user !== undefined);

          setFormData({
            ...eventDetails,
            users: usersFromEvent,
          });
          setSelectedUserIds(usersFromEvent.map((user) => user._id));
        } else {
          console.log("Failed to fetch event details");
          toast.error("Failed to fetch event details");
        }
      } catch (error) {
        console.log("Error fetching event details", error);
        toast.error("Error fetching event details");
      }
    };
    if (usersList.length > 0) {
      fetchEventDetails();
    }
  }, [eventId, dispatch, usersList]);

  const handleSelectedUser = (newValue) => {
    setSelectedUserIds(newValue.map((user) => user._id));
    setFormData({
      ...formData,
      users: newValue.map((user) => user.fullname),
    });
  };

  const handleBlur = async (e) => {
    const fieldName = e.target.name;
    try {
      await validationSchema.validateAt(fieldName, formData);
      setErr({ ...err, [fieldName]: "" });
    } catch (error) {
      setErr({ ...err, [fieldName]: error.message });
    }
  };

  const handleChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const [isDbBanner, setIsDbBanner] = useState(true);
  const handleImageChange = (e) => {
    setIsDbBanner(false);
    setFormData((prevformData) => ({
      ...prevformData,
      banner: e.target.files[0],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true); // Start loading
      // await validationSchema.validate(formData, { abortEarly: false });
      const updatedFormData = new FormData();

      Object.keys(formData).forEach((key) => {
        updatedFormData.append(key, formData[key]);
      });
      // formDataToSend.append("banner", banner);

      // Display the key/value pairs
      // for (var pair of formDataToSend.entries()) {
      //   console.log("formAppend", pair[0] + ":" + pair[1]);
      // }
      const isSuccess = await dispatch(
        updateEventAction(eventId, updatedFormData)
      );
      if (isSuccess) {
        toast.success("Event updated successfully!");
        setTimeout(() => {
          navigate("/event");
          window.location.reload();
        }, 1000);
      } else {
        toast.error("Something went wrong");
      }
    } catch (validationErrors) {
      // Handle validation errors
      const errors = {};
      if (validationErrors.inner) {
        validationErrors.inner.forEach((validationError) => {
          errors[validationError.path] = validationError.message;
        });
      }
      setErr(errors);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  return (
    <Box mt="1.5rem" mb="2.5rem" ml="2.5rem" mr="2.5rem">
      <Header title="EDIT EVENT" />
      <form onSubmit={handleSubmit}>
        <FormControl fullWidth variant="outlined" margin="normal">
          <Grid container spacing={2}>
            {/* Event Name */}
            <Grid item xs={12}>
              <label htmlFor="eventName">Event Name</label>
              <TextField
                id="eventName"
                name="eventName"
                value={formData.eventName}
                fullWidth
                margin="normal"
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {err.eventName && (
                <Grid item>
                  <Typography color="error">{err.eventName}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* Start Time */}
            <Grid item xs={6}>
              <label htmlFor="startTime">Start Time</label>
              <TextField
                id="startTime"
                name="startTime"
                value={formData.startTime}
                fullWidth
                placeholder="HH:MM AM/PM"
                margin="normal"
                // type="time"
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {err.startTime && (
                <Grid item>
                  <Typography color="error">{err.startTime}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* End Time */}
            <Grid item xs={6}>
              <label htmlFor="endTime">End Time</label>
              <TextField
                id="endTime"
                name="endTime"
                value={formData.endTime}
                fullWidth
                placeholder="HH:MM AM/PM"
                margin="normal"
                // type="time"
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {err.endTime && (
                <Grid item>
                  <Typography color="error">{err.endTime}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* Date */}
            <Grid item xs={12}>
              <label htmlFor="date">Date</label>
              <TextField
                id="date"
                name="date"
                value={formData.date}
                fullWidth
                margin="normal"
                type="date"
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {err.date && (
                <Grid item>
                  <Typography color="error">{err.date}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* Location */}
            <Grid item xs={12}>
              <label htmlFor="location">Location</label>
              <TextField
                id="location"
                name="location"
                value={formData.location}
                fullWidth
                margin="normal"
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {err.location && (
                <Grid item>
                  <Typography color="error">{err.location}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* Celebrity */}
            <Grid item xs={12}>
              <label htmlFor="users">Select the Celebrity</label>
              <Autocomplete
                multiple
                id="users"
                options={usersList}
                getOptionLabel={(option) => option.fullname}
                value={usersList.filter((user) =>
                  selectedUserIds.includes(user._id)
                )}
                onChange={(event, newValue) => {
                  handleSelectedUser(newValue);
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <ListItemText primary={option.fullname} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Celebrity"
                    name="users"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                  />
                )}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <label htmlFor="description">Description</label>
              <TextField
                id="description"
                name="description"
                value={formData.description}
                fullWidth
                margin="normal"
                onChange={handleChange}
              />
            </Grid>

            {/* UPLOAD BANNER */}
            {/* <Grid item xs={12}>
              <label
                htmlFor="banner"
                style={{ display: "flex", alignItems: "center" }}
              >
                <Button
                  variant="outlined"
                  color="info"
                  component="span"
                  size="large"
                  style={{
                    width: "100%",
                    fontSize: "1rem",
                    textTransform: "none",
                  }}
                >
                  Change Banner Image
                </Button>
                {isDbBanner ? (
                  <img
                    src={"http://localhost:8000/" + formData.banner}
                    // src={URL.createObjectURL(formData.banner)}
                    alt="Banner Image"
                    style={{
                      maxWidth: "100px",
                      maxHeight: "250px",
                      marginLeft: "1rem",
                    }}
                  />
                ) : (
                  <img
                    src={URL.createObjectURL(formData.banner)}
                    alt="Profile Picture Image"
                    style={{
                      maxWidth: "100px",
                      maxHeight: "250px",
                      marginLeft: "1rem",
                    }}
                  />
                )}
                <Input
                  id="banner"
                  name="banner"
                  type="file"
                  onChange={handleImageChange}
                  // onBlur={handleBlur}
                  inputProps={{ accept: "image/*" }}
                  style={{ display: "none" }}
                />
              </label>
            </Grid> */}
          </Grid>
        </FormControl>
        {/* SUBMIT BUTTON */}
        <Grid container justifyContent="center" alignItems="center">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={<SendIcon />}
          >
            Submit
          </Button>
        </Grid>
      </form>
      {loading && <LoadingIndicator />}
    </Box>
  );
};
export default EditEvent;
